import { Plus } from "lucide-react";

/**
 * HomeFaq — plain-language answers to the questions buyers (and AI
 * search engines) ask about the studio. Rendered as native <details>
 * so every answer is in the DOM, crawlable, and keyboard-accessible
 * without JS. The same array feeds the FAQPage JSON-LD on Home.
 *
 * Keep answers factual and consistent with the rest of the site
 * (quote-only pricing, 6–16 week delivery, one-business-day reply).
 */
export const HOME_FAQ: { q: string; a: string }[] = [
  {
    q: "What does NEXIRT do?",
    a: "NEXIRT is a custom software development studio based in Las Vegas, Nevada. We design, build, and run business systems — client portals, operations dashboards, payroll and workforce tools, mapping platforms, and marketing sites — and integrate AI agents into the workflows those systems support.",
  },
  {
    q: "What is AI agent workflow integration?",
    a: "It means connecting an AI model such as Claude to the tools your team already uses — your database, CRM, inbox, or ticket queue — so it can take real actions through structured tool-use, not just chat. Every action is scoped by permissions, logged for audit, and reviewed by a human where it matters.",
  },
  {
    q: "How long does a typical project take?",
    a: "Most systems ship in 6 to 16 weeks, depending on scope. Smaller engagements such as a marketing site or a single AI workflow can go live faster. You get a written plan and a fixed scope before any build starts.",
  },
  {
    q: "How much does custom software cost?",
    a: "Every project is quoted individually after a short discovery call, because scope drives cost. The quote is fixed for the agreed scope, so there are no surprise invoices. Tell the assistant what you need and we will reply with next steps within one business day.",
  },
  {
    q: "Where is our data hosted, and is it used to train AI?",
    a: "Systems are built AWS-native and can run inside your own AWS account. AI features use providers such as Anthropic and AWS Bedrock with no training on your data. Prompts sent to our site assistant are encrypted in transit and never used for training.",
  },
  {
    q: "Do you work with clients outside Las Vegas?",
    a: "Yes. NEXIRT works with businesses across the United States remotely, with the same process for local and out-of-state clients.",
  },
];

const CSS = `
  .nx-faq-item { border-bottom: 1px solid var(--nx-line); }
  .nx-faq-item summary { list-style: none; cursor: pointer; }
  .nx-faq-item summary::-webkit-details-marker { display: none; }
  .nx-faq-item summary:focus-visible { outline: 2px solid var(--nx-accent); outline-offset: 4px; border-radius: 6px; }
  .nx-faq-plus { transition: transform .25s ease, color .25s ease, border-color .25s ease; }
  .nx-faq-item[open] .nx-faq-plus { transform: rotate(45deg); color: var(--nx-text); border-color: color-mix(in srgb, var(--nx-accent) 50%, var(--nx-line)); }
  @media (prefers-reduced-motion: reduce) { .nx-faq-plus { transition: none; } }
`;

export function HomeFaq() {
  return (
    <section className="border-t border-nx-line/60" aria-labelledby="home-faq-h" data-home-faq>
      <style>{CSS}</style>
      <div className="nx-container grid gap-10 px-7 py-20 md:py-24 lg:grid-cols-[1fr_1.6fr] lg:gap-16">
        <div>
          <span className="nx-eyebrow">FAQ</span>
          <h2
            id="home-faq-h"
            className="mt-5 font-display text-[clamp(1.9rem,3.4vw,2.6rem)] font-semibold leading-[1.05] tracking-tight text-nx-text"
          >
            Questions, answered plainly.
          </h2>
          <p className="mt-4 max-w-sm text-nx-text-dim">
            Anything else? Ask the assistant at the top of the page, or email{" "}
            <a href="mailto:contact@nexirt.com" className="text-nx-text underline decoration-nx-line-strong underline-offset-4 hover:decoration-nx-accent">
              contact@nexirt.com
            </a>
            .
          </p>
        </div>
        <div className="border-t border-nx-line">
          {HOME_FAQ.map((f) => (
            <details key={f.q} className="nx-faq-item group">
              <summary className="flex items-center justify-between gap-6 py-5">
                <h3 className="text-[16px] font-medium text-nx-text md:text-[17px]">{f.q}</h3>
                <span className="nx-faq-plus grid h-7 w-7 shrink-0 place-items-center rounded-full border border-nx-line text-nx-text-dim" aria-hidden>
                  <Plus className="h-3.5 w-3.5" />
                </span>
              </summary>
              <p className="max-w-2xl pb-6 pr-10 text-[15px] leading-relaxed text-nx-text-dim">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export default HomeFaq;
